import {DialogTitle, DialogContent, Tabs, Tab, Grid} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {useContext, useState} from "react";
import {useTableData} from "../../../../misc/scroller/Table";
import {useProject, useProjectId} from "../ProjectName";
import {PrintManagerContext} from "../../../../misc/pdf/PrintManager";
import {first} from "nate-react-api-helpers";
import {tabGroup} from "../../../logistics/purchasing/PartsToSource";
import {TabWrapper} from "../TabWrapper";
import {useSubmittalPreferences} from "../../../../misc/pdf/useSubmittalPreferences";
import {SmallGreyButton} from "../../../../misc/SmallGreyButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {useCurrentContractor} from "../../quote/CurrentContractor";
import {Div10Row} from "../../../../api/QuoteDiv10";
import {div10TableName} from "./Div10Table";
import {useEventEmitter} from "../../../../misc/UseEventEmitter";
import {div10CurrentTab} from "./Div10";
import {getDiv10PDF} from "./pdf/Div10PDF";
import {defaultPages, PagesTab} from "./pdf/PagesTab";
import {ColumnTab} from "./pdf/ColumnTab";
import {PreambleTab} from "./pdf/PreambleTab";
import {getAll} from "../../../logistics/routes/useGetAll";
import {api} from "../../../../api/API";
import {useIsQuote} from "../../quote/QuoteContainer";
import {useAlternative} from "../../quote/alternative/Alternative";

export type ExportColumn = {
    display: string;
    key: keyof Div10Row;
    render?: (input: Div10Row) => string;
}

export const columns: ExportColumn[] = [{
    display: "Room",
    key: "room"
},{
    display: "Qty",
    key: "qty"
},{
    display: "Category",
    key: "productCategoryName"
},{
    display: "Product Code",
    key: "productCode"
},{
    display: "Finish",
    key: "finish"
},{
    display: "Description",
    key: "productName"
},{
    display: "Manufacturer",
    key: "manufacturerName"
},{
    display: "Notes",
    key: "note"
}]

const defaultColumns = [
    "room",
    "qty",
    "note"
]

export function MakeSubmittal(props: {
    onDone(): void;
}) {
    const project = useProjectId();
    const div10Tab = useEventEmitter(div10CurrentTab);
    const rows = useTableData(div10TableName(div10Tab || "", project)) as Div10Row[]

    return (
        <MakeSubmittalInner onDone={props.onDone} rows={rows} />
    )
}

export function MakeQuoteSubmittal(props: {}) {
    const [show, setShow] = useState(false);
    const project = useProjectId();
    const div10Tab = useEventEmitter(div10CurrentTab);
    const rows = useTableData(div10TableName(div10Tab || "", project)) as Div10Row[]

    return (
        <Grid item>
            <SmallGreyButton startIcon={<AddCircleIcon />} onClick={() => setShow(true)}>
                Create Submittal
            </SmallGreyButton>
            {show && <MakeSubmittalInner onDone={() => setShow(false)} rows={rows} />}
        </Grid>
    )
}

export function MakeSubmittalInner(props: {
    rows: Div10Row[]
    onDone(): void;
}) {
    const [selected, setSelected] = useState<ExportColumn[]>(
        defaultColumns
            .map(key => columns.find(c => c.key === key))
            .filter(v => !!v) as ExportColumn[]
    );
    const projectId = useProjectId();
    const printCtx = useContext(PrintManagerContext);
    const project = useProject();

    const prefs = useSubmittalPreferences({
        project: projectId,
        kind: "div10",
        onLoad: (list: ShopDrawingSubmittalPrefs) => {
            setSelected(list.columns.map(l => first(columns, c => c.key === l))
                .filter(v => !!v) as ExportColumn[])
            setPreamble(list.preamble)
            setPages(list.pages as any)
        }
    });

    const [preamble, setPreamble] = useState("")
    const [tab, setTab] = useState("pages");
    const [pages, setPages] = useState(defaultPages);

    const {getContractorDetails, CurrentContractorPromptElement} = useCurrentContractor({
        onAutoPromptCanceled: props.onDone
    });

    const isQuote = useIsQuote();
    const alternative = useAlternative();


    if(CurrentContractorPromptElement) {
        return (<>{CurrentContractorPromptElement}</>);
    }

    if(prefs.loading) return null;

    const submit = async () => {
        await prefs.save({
            version: 1,
            columns: selected.map(c => c.key),
            preamble: preamble,
            pages: pages,
        })

        if(!project) return;
        const {client, clientContact} = await getContractorDetails();

        const rows = isQuote ?
            await getAll(null, offset => api.quoteDiv10.list({
                project: projectId, alternative: alternative,
                offset: offset,
                tab: "wr-accessory"
            })) :
            await getAll(null, offset => api.div10.list({
                project: projectId, offset,
                tab: "wr-accessory"
            }))

        await printCtx.print2(getDiv10PDF({
            projectId: project.id,
            getData: async () => {
                return {
                    pages,
                    preamble,
                    columns: selected,
                    project,
                    lastSubmission: prefs.lastSubmission,
                    client,
                    clientContact,
                    data: rows,
                }
            }
        }));

        props.onDone();
    };

    return (
        <Dialog2 open maxWidth="md" fullWidth onClose={props.onDone} onEnterPress={submit}>
            <DialogTitle>Create Shop Drawing Submittal</DialogTitle>
            <TabWrapper>
                <Tabs className={tabGroup} value={tab} onChange={(e, value) => setTab(value)}>
                    <Tab label="Pages" value="pages" />
                    <Tab label="Preamble" value="preamble" />
                    <Tab label="Columns" value="columns" />
                </Tabs>
            </TabWrapper>
            <DialogContent>

                {tab === "pages" ?
                    <PagesTab value={pages} onChange={setPages} /> :
                        tab === "columns" ?
                            <ColumnTab value={selected} onChange={setSelected} sample={props.rows[0]} columns={columns} /> :
                                <PreambleTab value={preamble} onChange={setPreamble} />}
            </DialogContent>
            <SmoothActionBar
                actionName="Create and Download"
                onCancel={props.onDone}
                onSubmit={submit}
            />
        </Dialog2>
    )
}

export type ShopDrawingSubmittalPrefs = {
    version: 1
    columns: string[];
    preamble: string;
    pages: string[];
}
